import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link as LinkIcon, Loader2 } from "lucide-react";

import { api, errMsg, setAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";

// Landing page for the onboarding link HR emails to a new hire's personal
// address. Exchanges the link token for a session confined to the employee's
// own onboarding wizard, then hands over to the wizard. No login involved.
export default function OnboardingStartPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const ended = params.get("ended") === "1";
  const [error, setError] = useState(
    ended ? "Your onboarding session has ended. Open the link from your email again to continue." : token ? "" : "This link is incomplete.",
  );
  const started = useRef(false);

  useEffect(() => {
    if (!token || ended || started.current) return;
    started.current = true;
    api.post<{ accessToken: string }>("/onboarding/session", { token })
      .then(({ data }) => {
        setAccessToken(data.accessToken);
        navigate("/onboarding/wizard", { replace: true });
      })
      .catch((err) => {
        logError("OnboardingStartPage.session", err);
        setError(errMsg(err) ?? "This onboarding link could not be opened. Ask HR to send you a new one.");
      });
  }, [token, ended, navigate]);

  if (!error) {
    return (
      <div className="flex h-screen items-center justify-center gap-2 text-sm text-surface-500">
        <Loader2 size={16} className="animate-spin" /> Opening your onboarding form…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-50 px-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="items-center">
          <LinkIcon size={40} className="mb-2 text-surface-400" />
          <CardTitle>{ended ? "Session ended" : "Link unavailable"}</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-surface-400">
            The link stays valid for 7 days after HR sends it. If it has expired, ask HR to send a new one.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
